// Profile completion utilities
import { User, GetJobSeekerDetailsResponse } from '../types/api';
import { UserProfileData, AgentProfileFormData, CompanyProfileData } from '../types/components';
import { LocalStorageUser } from '../types/localStorage';

interface ProfileCompletionStatus {
  completed: boolean;
  percentage: number;
  missingFields: string[];
  completedFields: number;
  totalFields: number;
}

export const getProfileCompletionStatus = (user: LocalStorageUser | null, jobSeekerDetails: GetJobSeekerDetailsResponse | null): ProfileCompletionStatus => {
  if (!user) return { completed: false, percentage: 0, missingFields: [], completedFields: 0, totalFields: 0 };

  const missingFields: string[] = [];
  let completedFields = 0;
  let totalFields = 0;

  // Check basic profile fields
  totalFields += 4; // firstName, lastName, email, phone
  if (!user.firstName) missingFields.push('firstName');
  else completedFields++;

  if (!user.lastName) missingFields.push('lastName');
  else completedFields++;

  if (!user.email) missingFields.push('email');
  else completedFields++;

  if (!user.mobileNumber) missingFields.push('phone');
  else completedFields++;

  // Check role-specific fields
  if (user.role === 'Jobseeker') {
    totalFields += 5; // resume, skills, experience, education, preferences, professionalSummary
    // const userData: UserProfileData = JSON.parse(localStorage.getItem(`profileData_${user.role}_${user.userId}`) || '{}');

    if (!jobSeekerDetails?.attachments) missingFields.push('resume');
    else completedFields++;

    if (!jobSeekerDetails?.jobSeekerSkillDetails || jobSeekerDetails.jobSeekerSkillDetails.length === 0) missingFields.push('skills');
    else completedFields++;

    if (!jobSeekerDetails?.jobSeekerExperienceDetails?.yearsOfExperience && !jobSeekerDetails?.jobSeekerExperienceDetails?.isFresher) missingFields.push('experience');
    else completedFields++;

    if (!jobSeekerDetails?.jobSeekerQualificationDetails || jobSeekerDetails.jobSeekerQualificationDetails.length === 0) missingFields.push('education');
    else completedFields++;

    if (!jobSeekerDetails?.jobSeekerPreferredLocationDetails || jobSeekerDetails.jobSeekerPreferredLocationDetails.length === 0) missingFields.push('preferences');
    else completedFields++;

  } else if (user.role === 'Company') {
    totalFields += 8; // companyName, industry, companySize, description, location, website, legalDocuments, teamMembers, culture, verificationStatus
    const companyData: CompanyProfileData = JSON.parse(localStorage.getItem(`profileData_${user.role}_${user.userId}`) || '{}');

    if (!companyData.companyName) missingFields.push('companyName');
    else completedFields++;

    if (!companyData.industry) missingFields.push('industry');
    else completedFields++;

    if (!companyData.companySize) missingFields.push('companySize');
    else completedFields++;

    if (!companyData.description) missingFields.push('description');
    else completedFields++;

    if (!companyData.address) missingFields.push('location'); // Assuming location maps to address
    else completedFields++;

    if (!companyData.website) missingFields.push('website');
    else completedFields++;

    if (!companyData.gstNumber) missingFields.push('legalDocuments'); // Simplified to gstNumber
    else completedFields++;

    // if (!companyData.teamMembers || companyData.teamMembers.length === 0) missingFields.push('teamMembers');
    // else completedFields++;

    // if (!companyData.missionStatement) missingFields.push('culture'); // Simplified to missionStatement
    // else completedFields++;

    if (!companyData.verificationStatus) missingFields.push('verificationStatus');
    else completedFields++;

  } else if (user.role === 'Agent') {
    totalFields += 10; // agencyName, experience, specializations, credentials, services, portfolio, references, verificationStatus, feeStructure, availability
    const agentData: AgentProfileFormData = JSON.parse(localStorage.getItem(`profileData_${user.role}_${user.userId}`) || '{}');

    if (!agentData.agencyName) missingFields.push('agencyName');
    else completedFields++;

    if (!agentData.experience) missingFields.push('experience');
    else completedFields++;

    if (!agentData.specializations || agentData.specializations.length === 0) missingFields.push('specializations');
    else completedFields++;

    if (!agentData.credentials) missingFields.push('credentials');
    else completedFields++;

    if (!agentData.services || agentData.services.length === 0) missingFields.push('services');
    else completedFields++;

    if (!agentData.portfolio) missingFields.push('portfolio');
    else completedFields++;

    if (!agentData.references) missingFields.push('references');
    else completedFields++;

    if (!agentData.profileVisibility) missingFields.push('verificationStatus'); // Simplified to profileVisibility
    else completedFields++;

    if (!agentData.feeStructure) missingFields.push('feeStructure');
    else completedFields++;

    if (!agentData.availability) missingFields.push('availability');
    else completedFields++;
  }

  const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  const completed = percentage >= 80; // Consider profile complete if 80% or more fields are filled

  return {
    completed,
    percentage,
    missingFields,
    completedFields,
    totalFields
  };
};

export const shouldShowProfileCompletionModal = (user: LocalStorageUser | null): boolean => {
  if (!user) return false;
  
  // Check if profile completion was already marked as done
  const profileCompletedKey = `profileCompleted_${user.role}_${user.userId}`;
  const profileCompleted: string | null = localStorage.getItem(profileCompletedKey);
  const legacyCompleted = localStorage.getItem('userProfileComplete') === 'true';
  const hasLegacyData = !!localStorage.getItem('userProfileData');
  const hasPerUserData = !!localStorage.getItem(`profileData_${user.role}_${user.userId}`);
  
  // If any completion indicator exists, don't show
  if (profileCompleted || legacyCompleted || hasLegacyData || hasPerUserData) {
    return false;
  }
  
  // For new users (agents, companies, users), show the modal if they haven't completed it
  // This ensures the modal shows for any user who logs in for the first time
  if (user.role === 'Agent' || user.role === 'Company' || user.role === 'Jobseeker') {
    return true;
  }
  
  // Fallback: check if they have basic info but no profile completion
  const hasBasicInfo = user.firstName!="" && user.lastName!="" && user.email!="";
  return hasBasicInfo && !profileCompleted;
};

export const markProfileAsCompleted = (user: LocalStorageUser | null): void => {
  if (user) {
    localStorage.setItem(`profileCompleted_${user.role}_${user.userId}`, 'true');
  }
};
